/**
 * ─── Pawnshop Domain Service ────────────────────────────────
 * Core business logic: interest computation, maturity checking,
 * redemption calculation, auction processing.
 *
 * DDD: Domain Service for PawnTicket aggregate.
 * All computations are pure functions — no side effects.
 * Double-entry: each operation describes its ledger effect.
 */

import type {
  PawnTicket, PawnItem, PawnPayment, AuctionItem,
  PawnshopConfig,
} from './pawnshop.schema'
import { DEFAULT_PAWNSHOP_CONFIG } from './pawnshop.schema'

export interface LedgerEntry {
  account: string
  debit: number
  credit: number
  description: string
}

export interface ServiceResult<T> {
  success: boolean
  data?: T
  error?: string
  ledgerEntries?: LedgerEntry[]
}

export class PawnshopService {
  private config: PawnshopConfig

  constructor(config: PawnshopConfig = DEFAULT_PAWNSHOP_CONFIG) {
    this.config = config
  }

  // ─── Ticket Creation ────────────────────────────────────

  /**
   * Compute ticket financials: net proceeds, maturity date, advance interest.
   */
  computeTicketFinancials(params: {
    principalAmount: number
    interestRate: number
    advanceInterestMonths: number
    serviceChargeRate: number
    pawnDate: number
    gracePeriodDays: number
  }): {
    advanceInterest: number
    serviceCharge: number
    netProceeds: number
    maturityDate: number
    expiryDate: number
  } {
    const advanceInterest = Math.round(
      params.principalAmount * params.interestRate * params.advanceInterestMonths * 100
    ) / 100
    const serviceCharge = Math.round(params.principalAmount * params.serviceChargeRate * 100) / 100
    const netProceeds = Math.round(
      (params.principalAmount - advanceInterest - serviceCharge) * 100
    ) / 100

    const maturityDate = params.pawnDate + 30 * 86400000 // 30 days standard
    const expiryDate = maturityDate + params.gracePeriodDays * 86400000

    return { advanceInterest, serviceCharge, netProceeds, maturityDate, expiryDate }
  }

  /**
   * Create a new pawn ticket with double-entry.
   */
  createTicket(params: {
    customerId: string
    customerName: string
    itemIds: string[]
    principalAmount: number
    interestRate?: number
    interestType?: 'monthly' | 'bi_monthly' | 'custom'
    advanceInterestMonths?: number
    pawnDate?: number
    notes?: string
  }): ServiceResult<Partial<PawnTicket>> {
    const rate = params.interestRate ?? this.config.defaultInterestRate
    const months = params.advanceInterestMonths ?? 1
    const pawnDate = params.pawnDate ?? Date.now()

    if (rate > this.config.maxInterestRate) {
      return { success: false, error: `Interest rate exceeds maximum ${this.config.maxInterestRate * 100}%` }
    }

    const financials = this.computeTicketFinancials({
      principalAmount: params.principalAmount,
      interestRate: rate,
      advanceInterestMonths: months,
      serviceChargeRate: this.config.serviceChargeRate,
      pawnDate,
      gracePeriodDays: this.config.defaultGracePeriodDays,
    })

    const ticketNumber = `PT-${new Date(pawnDate).toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`

    const ticket: Partial<PawnTicket> = {
      ticketNumber,
      customerId: params.customerId,
      customerName: params.customerName,
      itemIds: params.itemIds,
      principalAmount: params.principalAmount,
      interestRate: rate,
      interestType: params.interestType ?? 'monthly',
      advanceInterestMonths: months,
      serviceCharge: financials.serviceCharge,
      netProceeds: financials.netProceeds,
      pawnDate,
      maturityDate: financials.maturityDate,
      expiryDate: financials.expiryDate,
      gracePeriodDays: this.config.defaultGracePeriodDays,
      totalRedemptionAmount: params.principalAmount,
      totalPaymentsMade: 0,
      remainingBalance: params.principalAmount,
      status: 'active',
      renewalCount: 0,
      notes: params.notes,
    }

    const ledgerEntries: LedgerEntry[] = [
      { account: 'Customer Loan Receivable', debit: params.principalAmount, credit: 0, description: `Pawn ticket ${ticketNumber}` },
      { account: 'Cash', debit: 0, credit: financials.netProceeds, description: `Net proceeds ${ticketNumber}` },
      { account: 'Interest Income', debit: 0, credit: financials.advanceInterest, description: `Advance interest ${ticketNumber}` },
      { account: 'Service Charge Income', debit: 0, credit: financials.serviceCharge, description: `Service charge ${ticketNumber}` },
    ]

    return { success: true, data: ticket, ledgerEntries }
  }

  // ─── Interest Computation ────────────────────────────────

  /**
   * Compute accrued interest since pawn date or last payment.
   */
  computeAccruedInterest(ticket: PawnTicket, asOfDate: number = Date.now()): number {
    const monthsElapsed = Math.max(0, Math.ceil((asOfDate - ticket.pawnDate) / (30 * 86400000)))
    const totalInterestDue = Math.round(ticket.principalAmount * ticket.interestRate * monthsElapsed * 100) / 100
    return Math.max(0, totalInterestDue)
  }

  /**
   * Compute total redemption amount (principal + accrued interest - payments made).
   */
  computeRedemptionAmount(ticket: PawnTicket, asOfDate: number = Date.now()): number {
    const accruedInterest = this.computeAccruedInterest(ticket, asOfDate)
    const totalDue = ticket.principalAmount + accruedInterest
    return Math.max(0, Math.round((totalDue - ticket.totalPaymentsMade) * 100) / 100)
  }

  // ─── Redemption ──────────────────────────────────────────

  /**
   * Fully redeem a ticket.
   */
  redeemTicket(ticket: PawnTicket, payments: PawnPayment[], redeemDate: number = Date.now()): ServiceResult<Partial<PawnTicket>> {
    if (ticket.status !== 'active' && ticket.status !== 'renewed') {
      return { success: false, error: `Ticket is ${ticket.status}, cannot redeem` }
    }

    const redemptionAmount = this.computeRedemptionAmount(ticket, redeemDate)
    const totalPaid = payments.reduce((s, p) => s + p.amount, 0)

    if (totalPaid < redemptionAmount) {
      return {
        success: false,
        error: `Insufficient payment. Required: ₱${redemptionAmount.toLocaleString()}, Paid: ₱${totalPaid.toLocaleString()}`,
      }
    }

    const ledgerEntries: LedgerEntry[] = [
      { account: 'Cash', debit: totalPaid, credit: 0, description: `Redemption ${ticket.ticketNumber}` },
      { account: 'Customer Loan Receivable', debit: 0, credit: ticket.principalAmount, description: `Loan repaid ${ticket.ticketNumber}` },
      { account: 'Interest Income', debit: 0, credit: (totalPaid - ticket.principalAmount), description: `Interest earned ${ticket.ticketNumber}` },
    ]

    const updated: Partial<PawnTicket> = {
      status: 'redeemed',
      redeemedDate: redeemDate,
      totalRedemptionAmount: redemptionAmount,
      totalPaymentsMade: totalPaid,
      remainingBalance: 0,
    }

    return { success: true, data: updated, ledgerEntries }
  }

  // ─── Renewal ─────────────────────────────────────────────

  /**
   * Renew a ticket: pay outstanding interest, extend maturity.
   */
  renewTicket(ticket: PawnTicket, renewalDate: number = Date.now()): ServiceResult<Partial<PawnTicket>> {
    if (!this.config.allowRenewal) {
      return { success: false, error: 'Renewal is not allowed' }
    }
    if (ticket.renewalCount >= this.config.maxRenewals) {
      return { success: false, error: `Maximum renewals (${this.config.maxRenewals}) reached` }
    }
    if (ticket.status !== 'active' && ticket.status !== 'renewed') {
      return { success: false, error: `Cannot renew ${ticket.status} ticket` }
    }

    const outstandingInterest = this.computeAccruedInterest(ticket, renewalDate)

    const newMaturity = renewalDate + 30 * 86400000
    const newExpiry = newMaturity + this.config.defaultGracePeriodDays * 86400000

    const updated: Partial<PawnTicket> = {
      status: 'renewed',
      renewalCount: ticket.renewalCount + 1,
      renewedDate: renewalDate,
      maturityDate: newMaturity,
      expiryDate: newExpiry,
    }

    const ledgerEntries: LedgerEntry[] = [
      { account: 'Cash', debit: outstandingInterest, credit: 0, description: `Renewal interest ${ticket.ticketNumber}` },
      { account: 'Interest Income', debit: 0, credit: outstandingInterest, description: `Renewal interest earned ${ticket.ticketNumber}` },
    ]

    return { success: true, data: updated, ledgerEntries }
  }

  // ─── Auction ─────────────────────────────────────────────

  /**
   * Process an auction sale. Compute surplus to return to pawner.
   */
  processAuction(params: {
    ticket: PawnTicket
    item: PawnItem
    soldAmount: number
    buyerName: string
    buyerContact?: string
    soldDate?: number
  }): ServiceResult<Partial<AuctionItem>> {
    const { ticket, item, soldAmount, buyerName, buyerContact } = params
    const soldDate = params.soldDate ?? Date.now()

    const outstandingBalance = this.computeRedemptionAmount(ticket, soldDate)
    const surplus = Math.max(0, soldAmount - outstandingBalance)
    const surplusToReturn = this.config.auctionSurplusPolicy === 'return' ? surplus : 0

    const auctionItem: Partial<AuctionItem> = {
      ticketId: ticket.id,
      ticketNumber: ticket.ticketNumber,
      itemId: item.id,
      itemDescription: item.description,
      originalAppraisalValue: item.appraisalValue,
      minimumBid: Math.round(outstandingBalance * 100) / 100,
      soldAmount,
      auctionDate: soldDate,
      soldDate,
      buyerName,
      buyerContact,
      surplusReturned: surplusToReturn,
      status: 'sold',
    }

    const ledgerEntries: LedgerEntry[] = [
      { account: 'Cash', debit: soldAmount, credit: 0, description: `Auction sale ${item.description}` },
      { account: 'Customer Loan Receivable', debit: 0, credit: Math.min(soldAmount, outstandingBalance), description: `Loan recovered ${ticket.ticketNumber}` },
    ]
    if (surplusToReturn > 0) {
      ledgerEntries.push({
        account: 'Customer Surplus Payable', debit: 0, credit: surplusToReturn,
        description: `Surplus to ${ticket.customerName}`,
      })
    }

    return { success: true, data: auctionItem, ledgerEntries }
  }

  // ─── Dashboard Statistics ────────────────────────────────

  /**
   * Compute pawnshop dashboard KPIs.
   */
  computeDashboardStats(tickets: PawnTicket[], items: PawnItem[], payments: PawnPayment[]): {
    activeTickets: number
    totalPrincipalOutstanding: number
    totalInterestEarned: number
    itemsInVault: number
    expiringSoon: number
    overdueCount: number
    redemptionRate: number
  } {
    const now = Date.now()
    const active = tickets.filter(t => t.status === 'active' || t.status === 'renewed')
    const redeemed = tickets.filter(t => t.status === 'redeemed')
    const pawnedItems = items.filter(i => i.status === 'pawned')
    const expiringSoon = active.filter(t => t.maturityDate > now && t.maturityDate < now + 7 * 86400000)
    const overdue = active.filter(t => t.maturityDate < now)

    const interestEarned = payments
      .filter(p => p.paymentType === 'interest' || p.paymentType === 'renewal')
      .reduce((s, p) => s + p.amount, 0)

    return {
      activeTickets: active.length,
      totalPrincipalOutstanding: active.reduce((s, t) => s + t.principalAmount, 0),
      totalInterestEarned: interestEarned,
      itemsInVault: pawnedItems.length,
      expiringSoon: expiringSoon.length,
      overdueCount: overdue.length,
      redemptionRate: tickets.length > 0 ? Math.round(redeemed.length / (redeemed.length + active.length) * 100) : 0,
    }
  }
}
